import { tokenStorage } from '@/lib/tokens';
import { api } from '../api';
import type { ActiveTenantResponse, EffectivePermission, LoginInput, LoginResponse, Me, PlatformTenant } from '@/types/auth';
import type { PortalScopeResponse } from '@/types/domain';

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginInput>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
    }),
    logout: builder.mutation<{ ok: boolean }, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
        body: { refreshToken: tokenStorage.getRefresh() ?? '' },
      }),
    }),
    me: builder.query<Me, void>({
      query: () => '/auth/me',
      providesTags: ['Me'],
    }),
    myPermissions: builder.query<EffectivePermission[], void>({
      query: () => '/auth/me/permissions',
      providesTags: ['Permissions'],
    }),
    setupTotp: builder.mutation<{ secret: string; otpauth: string }, void>({
      query: () => ({ url: '/auth/totp/setup', method: 'POST' }),
    }),
    verifyTotp: builder.mutation<{ ok: boolean }, { code: string }>({
      query: (body) => ({ url: '/auth/totp/verify', method: 'POST', body }),
      invalidatesTags: ['Me'],
    }),
    disableTotp: builder.mutation<{ ok: boolean }, { code: string }>({
      query: (body) => ({ url: '/auth/totp/disable', method: 'POST', body }),
      invalidatesTags: ['Me'],
    }),
    forgotPassword: builder.mutation<Record<string, never>, { email: string }>({
      query: (body) => ({ url: '/auth/password/forgot', method: 'POST', body }),
    }),
    resetPassword: builder.mutation<{ ok: boolean }, { token: string; newPassword: string }>({
      query: (body) => ({ url: '/auth/password/reset', method: 'POST', body }),
    }),
    changePassword: builder.mutation<{ ok: boolean }, { oldPassword: string; newPassword: string }>({
      query: (body) => ({ url: '/auth/password/change', method: 'POST', body }),
    }),
    getActiveTenant: builder.query<ActiveTenantResponse, void>({
      query: () => '/me/active-tenant',
      providesTags: ['ActiveTenant'],
    }),
    setActiveTenant: builder.mutation<ActiveTenantResponse, { tenantId: number }>({
      query: (body) => ({ url: '/me/active-tenant', method: 'PUT', body }),
      invalidatesTags: ['ActiveTenant', 'Me'],
    }),
    listPlatformTenants: builder.query<PlatformTenant[], void>({
      query: () => '/platform/tenants',
      providesTags: ['PlatformTenants'],
    }),
    getPortalScope: builder.query<PortalScopeResponse, void>({
      query: () => '/me/portal-scope',
      providesTags: ['PortalScope'],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useMeQuery,
  useMyPermissionsQuery,
  useSetupTotpMutation,
  useVerifyTotpMutation,
  useDisableTotpMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useGetActiveTenantQuery,
  useSetActiveTenantMutation,
  useListPlatformTenantsQuery,
  useGetPortalScopeQuery,
} = authApi;
